import { apply, emptyState, type GroupState, type StoredEvent } from "@split/core";

/** What the last fold produced, so the next one only has to apply the new events. */
export interface FoldCache {
  groupId: string;
  count: number;
  /** The last event folded in: the cache is only reused while the list still starts the same. */
  lastId: string | null;
  state: GroupState;
}

/**
 * Folds a group's confirmed events into its state, reusing the previous fold when the event
 * list only grew. Anything else (another group's events still showing while a group opens, a
 * shorter list, a different history) starts again from nothing, so one group's state is never
 * built on top of another's.
 */
export function foldEvents(
  cache: FoldCache | null,
  groupId: string,
  events: readonly StoredEvent[],
): FoldCache {
  const reusable =
    cache !== null &&
    cache.groupId === groupId &&
    cache.count <= events.length &&
    (cache.count === 0 ? true : events[cache.count - 1]?.id === cache.lastId) &&
    (events.length === 0 || events[0]!.groupId === groupId);
  if (!reusable) cache = { groupId, count: 0, lastId: null, state: emptyState(groupId) };
  if (cache!.count === events.length) return cache!;
  let state = structuredClone(cache!.state);
  for (let i = cache!.count; i < events.length; i++) {
    if (events[i]!.groupId !== groupId) continue; // never fold another group's events in
    state = apply(state, events[i]!);
  }
  return { groupId, count: events.length, lastId: events.at(-1)?.id ?? null, state };
}
